import { useEffect, useRef } from "react";
import { motion, useInView, useReducedMotion } from "motion/react";
import { ClipPhoto, EASE_OUT, MaskWords, Photo } from "../../../kit.jsx";
import { ArrowLink } from "../shared.jsx";
import { AWARDS, BUILDING, TORCHES } from "./album.js";
import { FigureGlyph } from "./Isotype.jsx";

const IN_OUT = [0.65, 0, 0.35, 1];

// Plays motion once the element is well into view. With reduced motion it starts in the end state.
function usePlay(amount = 0.45) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const inView = useInView(ref, { once: true, amount });
  return { ref, play: reduceMotion || inView, still: reduceMotion };
}

const draw = (play, still, delay = 0, duration = 1) => ({
  initial: still ? false : { pathLength: 0 },
  animate: play ? { pathLength: 1 } : { pathLength: 0 },
  transition: { duration, delay, ease: IN_OUT },
});

const pop = (play, still, delay = 0) => ({
  initial: still ? false : { scale: 0, opacity: 0 },
  animate: play ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 },
  transition: { duration: 0.45, delay, ease: EASE_OUT },
});

/* ---------- AAROH ---------- */

function DaysDial({ play, still }) {
  return (
    <svg viewBox="0 0 160 104" aria-hidden="true">
      <path className="ab-dial__base" d="M0 82 H160" />
      <motion.path className="ab-dial__line" d="M8 82 A32 32 0 0 1 72 82" {...draw(play, still, 0.1, 0.9)} />
      <motion.path className="ab-dial__line" d="M88 82 A32 32 0 0 1 152 82" {...draw(play, still, 0.7, 0.9)} />
      <motion.circle className="ab-dial__sun" cx="40" cy="50" r="7" {...pop(play, still, 0.6)} />
      <motion.circle className="ab-dial__sun" cx="120" cy="50" r="7" {...pop(play, still, 1.2)} />
      <text x="40" y="100" textAnchor="middle">1 Aug</text>
      <text x="120" y="100" textAnchor="middle">2 Aug</text>
    </svg>
  );
}

const TRACKS = ["AI / ML", "Game development", "Cybersecurity", "Hardware"];

function TracksDial({ play, still }) {
  return (
    <div className="ab-dial__tracks">
      <svg viewBox="0 0 104 104" aria-hidden="true">
        {TRACKS.map((track, i) => (
          <motion.circle
            key={track}
            className={`ab-dial__ring is-${i}`}
            cx="52"
            cy="52"
            r={46 - i * 11}
            transform="rotate(-90 52 52)"
            {...draw(play, still, 0.15 + i * 0.22, 0.9)}
          />
        ))}
      </svg>
      <ol>
        {TRACKS.map((track, i) => (
          <li key={track}><span className={`ab-dial__swatch is-${i}`} aria-hidden="true" />{track}</li>
        ))}
      </ol>
    </div>
  );
}

function Trophy() {
  return (
    <>
      <path d="M5 2 H17 V8 Q17 14.5 11 14.5 Q5 14.5 5 8 Z" />
      <path d="M5 4.5 H2 Q2 9.5 5.5 10 M17 4.5 H20 Q20 9.5 16.5 10" className="ab-trophy__handle" />
      <path d="M9.5 14.5 H12.5 V18.5 H9.5 Z" />
      <path d="M5.5 18.5 H16.5 V22 H5.5 Z" />
    </>
  );
}

function TrophiesDial({ play, still }) {
  return (
    <svg viewBox="0 0 168 56" aria-hidden="true">
      {Array.from({ length: 13 }, (_, i) => (
        <g key={i} transform={`translate(${(i % 7) * 24} ${Math.floor(i / 7) * 28 + (i >= 7 ? 0 : 0)})`}>
          <motion.g className="ab-trophy" {...pop(play, still, 0.1 + i * 0.07)}>
            <Trophy />
          </motion.g>
        </g>
      ))}
    </svg>
  );
}

function AgeDial({ play, still }) {
  return (
    <svg viewBox="0 0 104 104" aria-hidden="true">
      <circle className="ab-dial__base" cx="52" cy="52" r="44" />
      <motion.circle className="ab-dial__line" cx="52" cy="52" r="44" transform="rotate(-90 52 52)" {...draw(play, still, 0.2, 1.2)} />
      <text x="52" y="66" textAnchor="middle" className="ab-dial__age">15</text>
    </svg>
  );
}

const DIALS = [
  { key: "days", value: "2", label: "days at IIIT Delhi, 1–2 August 2026", Dial: DaysDial },
  { key: "tracks", value: "4", label: "innovation tracks", Dial: TracksDial },
  { key: "teams", value: "13", label: "teams recognised", Dial: TrophiesDial },
  { key: "age", value: "15", label: "the age of the youngest participants", Dial: AgeDial },
];

function ClipTile({ clip }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const inView = useInView(ref, { amount: 0.25 });
  useEffect(() => {
    const video = ref.current;
    if (!video || reduceMotion) return;
    if (inView) video.play().catch(() => {});
    else video.pause();
  }, [inView, reduceMotion]);
  return (
    <figure className="ab-aa__tile ab-aa__tile--clip is-lead">
      {reduceMotion ? (
        <img src={clip.poster} alt={clip.alt} width={clip.width} height={clip.height} loading="lazy" decoding="async" />
      ) : (
        <video ref={ref} src={clip.src} poster={clip.poster} muted loop playsInline preload="none" aria-label={clip.alt} />
      )}
      <figcaption>{clip.caption}</figcaption>
    </figure>
  );
}

function AlbumTile({ photo, className = "" }) {
  return (
    <figure className={`ab-aa__tile ${className}`}>
      <Photo src={photo.srcSmall ?? photo.src} alt={photo.alt} width={photo.width} height={photo.height} sizes="(max-width: 767px) 50vw, 25vw" />
    </figure>
  );
}

export function AarohNumbers() {
  const { ref, play, still } = usePlay(0.35);
  return (
    <section className="ab-aa" id="aaroh" aria-labelledby="ab-aa-title">
      <header className="ab-aa__head">
        <div>
          <p className="ab-kicker">AAROH · IIIT Delhi · 1–2 August 2026</p>
          <MaskWords id="ab-aa-title" className="ab-h2" text="363 young innovators, counted another way." />
        </div>
        <p className="ab-body">
          GRID organised AAROH with Tachyon India and CiPD, IIIT Delhi. School students did most of the organising.
        </p>
      </header>
      <ul className="ab-dials" ref={ref}>
        {DIALS.map(({ key, value, label, Dial }, i) => (
          <motion.li
            key={key}
            initial={still ? false : { opacity: 0, y: 20 }}
            animate={play ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.7, delay: i * 0.08, ease: EASE_OUT }}
          >
            <div className={`ab-dial ab-dial--${key}`}><Dial play={play} still={still} /></div>
            <strong>{value}</strong>
            <span>{label}</span>
          </motion.li>
        ))}
      </ul>
      <div className="ab-aa__mosaic">
        {TORCHES ? <ClipTile clip={TORCHES} /> : <AlbumTile photo={BUILDING[0]} className="is-lead" />}
        {BUILDING.map((photo) => <AlbumTile key={photo.id} photo={photo} />)}
      </div>
      <div className="ab-aa__awards">
        <div className="ab-aa__awards-head">
          <strong>13 teams recognised.</strong>
          <span>Maj Gen (Retd.) P. K. Sehgal felicitated the top teams on stage.</span>
        </div>
        <ul>
          {AWARDS.map((photo, i) => (
            <motion.li
              key={photo.id}
              initial={still ? false : { opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: EASE_OUT }}
            >
              <AlbumTile photo={photo} />
              <small>{photo.caption}</small>
            </motion.li>
          ))}
        </ul>
      </div>
      <div className="ab-aa__links">
        <ArrowLink to="/programmes/aaroh">Read the AAROH recap</ArrowLink>
        <ArrowLink to="/media">Every photo</ArrowLink>
      </div>
    </section>
  );
}

/* ---------- Aurora ---------- */

const STAGES = ["Idea round", "Prototype", "Live pitch", "Incubation"];

export function AuroraStory() {
  const coins = usePlay(0.5);
  const stages = usePlay(0.5);
  const dots = usePlay(0.5);
  return (
    <section className="ab-au" id="aurora" aria-labelledby="ab-au-title">
      <header className="ab-au__head">
        <p className="ab-kicker">Aurora · GRID's own programme · online</p>
        <MaskWords id="ab-au-title" className="ab-h2" text="A global hackathon, from idea to incubation." />
      </header>
      <div className="ab-au__grid">
        <div className="ab-au__prize" ref={coins.ref}>
          <svg viewBox="0 0 240 96" aria-hidden="true">
            {[0].map((i) => (
              <g key={i} transform="translate(120 48)">
                <motion.g className="ab-coin" {...pop(coins.play, coins.still, 0.1 + i * 0.16)}>
                  <circle r="40" />
                  <circle r="31" className="ab-coin__inner" />
                  <text y="9" textAnchor="middle">₹1L</text>
                </motion.g>
              </g>
            ))}
          </svg>
          <strong>₹1 lakh</strong>
          <span>in prizes, given as HP laptops to the winners.</span>
        </div>
        <div className="ab-au__stages" ref={stages.ref}>
          <ol>
            <span className="ab-au__track" aria-hidden="true">
              <motion.span
                initial={stages.still ? false : { scaleX: 0 }}
                animate={stages.play ? { scaleX: 1 } : { scaleX: 0 }}
                transition={{ duration: 1.4, delay: 0.1, ease: IN_OUT }}
              />
            </span>
            {STAGES.map((stage, i) => (
              <li key={stage}>
                <motion.span className="ab-au__node" aria-hidden="true" {...pop(stages.play, stages.still, 0.2 + i * 0.36)} />
                <strong>{stage}</strong>
              </li>
            ))}
          </ol>
          <p>Teams moved from an idea round into prototypes, live pitches and incubation.</p>
        </div>
        <div className="ab-au__top" ref={dots.ref}>
          <svg viewBox="0 0 260 64" aria-hidden="true">
            {Array.from({ length: 20 }, (_, i) => (
              <g key={i} transform={`translate(${13 + (i % 10) * 26} ${16 + Math.floor(i / 10) * 30})`}>
                <motion.circle r={i === 0 ? 11 : 9} className={i === 0 ? "ab-au__dot is-winner" : "ab-au__dot"} {...pop(dots.play, dots.still, 0.1 + i * 0.04)} />
              </g>
            ))}
          </svg>
          <strong>Top 20</strong>
          <span>teams entered incubation</span>
          <p><span className="ab-au__mark" aria-hidden="true" />Overall winner: Jerovin Floyd Vincent Joseph, for ChillGui, which makes everyday apps easier for older people to use.</p>
        </div>
        <ClipPhoto
          className="ab-au__photo"
          src="/images/grid-photo-10-p8.jpg"
          alt="Aurora overall winner Jerovin Floyd Vincent Joseph holding his prize laptop"
          caption="Jerovin Floyd with his winner's laptop."
          sizes="(max-width: 900px) 100vw, 30vw"
        />
      </div>
      <p className="ab-au__note">
        1,500 participants from 27+ countries. <ArrowLink to="/programmes/aurora">Aurora's results</ArrowLink>
      </p>
    </section>
  );
}

/* ---------- Metamorphosis ---------- */

const jitter = (i, s) => {
  const v = Math.sin((i + 1) * 12.9898 * s) * 43758.5453;
  return v - Math.floor(v) - 0.5;
};

export function MetamorphosisStory() {
  const tiles = usePlay(0.4);
  const people = usePlay(0.6);
  return (
    <section className="ab-mt" id="metamorphosis" aria-labelledby="ab-mt-title">
      <header className="ab-mt__head">
        <span className="ab-mt__role">GRID's role: outreach partner</span>
        <MaskWords id="ab-mt-title" className="ab-h2" text="Metamorphosis, a programme we helped fill." />
        <p className="ab-body">
          Tale of Humankind Foundation and IIT Delhi's Office of Academic Outreach & New Initiatives organised it. GRID was the outreach partner and helped bring students in.
        </p>
      </header>
      <div className="ab-mt__grid">
        <figure className="ab-mt__products" ref={tiles.ref}>
          <svg viewBox="0 0 324 144" role="img" aria-label="36 small tiles, one for each product">
            {Array.from({ length: 36 }, (_, i) => {
              const x = (i % 9) * 36;
              const y = Math.floor(i / 9) * 36;
              return (
                <g key={i} transform={`translate(${x + 3} ${y + 3})`}>
                  <motion.g
                    initial={tiles.still ? false : { opacity: 0, x: jitter(i, 1) * 160, y: jitter(i, 2) * 120, rotate: jitter(i, 3) * 60 }}
                    animate={tiles.play ? { opacity: 1, x: 0, y: 0, rotate: 0 } : undefined}
                    transition={{ duration: 0.8, delay: 0.05 + i * 0.03, ease: EASE_OUT }}
                  >
                    <rect className="ab-mt__tile" width="30" height="30" rx="7" />
                  </motion.g>
                </g>
              );
            })}
          </svg>
          <figcaption>
            <strong>36</strong>
            <span>products from one week of building.</span>
          </figcaption>
        </figure>
        <figure className="ab-mt__people" ref={people.ref}>
          <svg viewBox="0 0 330 40" role="img" aria-label="15 outlined figures, one for every ten students">
            {Array.from({ length: 15 }, (_, i) => (
              <g key={i} transform={`translate(${i * 22} 0)`}>
                <motion.g className="ab-fig is-meta is-on" {...pop(people.play, people.still, 0.05 + i * 0.05)}>
                  <g className="ab-fig__body"><FigureGlyph /></g>
                </motion.g>
              </g>
            ))}
          </svg>
          <figcaption>
            <strong>150</strong>
            <span>students from 18 states, over one week at IIT Delhi. One figure is ten students.</span>
          </figcaption>
        </figure>
        <ClipPhoto
          className="ab-mt__photo"
          src="/images/metamorphosis/album/meta-510-wearable-prototype-test-1600.webp"
          alt="A student tries out a wearable prototype at the Metamorphosis project showcase at IIT Delhi"
          caption="Metamorphosis at IIT Delhi. GRID was the outreach partner."
          sizes="(max-width: 900px) 100vw, 50vw"
        />
      </div>
      <ArrowLink to="/programmes/metamorphosis">How GRID supported it</ArrowLink>
    </section>
  );
}
